/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Flex,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Fragment } from "react";
import _ from "lodash";

import Card from "components/card/Card";
import { useProjectsTable } from "./projectsTable/hooks/useProjectsTable";
import { createProjectsTableColumns } from "./projectsTable/helpers/tableColumns";
import { isExpeditedOrder } from "./projectsTable/helpers/dataTransform";
import { ProjectsTableProps } from "./projectsTable/types";


export default function ProjectsTable({
  isFetching = false,
  tableData,
  onShowProjectDetails,
  pageLimit,
}: ProjectsTableProps) {
  const { data, textColor, unreadMessages, columnWidths } = useProjectsTable(
    tableData,
    onShowProjectDetails
  );

  const columns = createProjectsTableColumns(
    textColor,
    unreadMessages,
    onShowProjectDetails
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  return (
    <Card
      flexDirection="column"
      w="calc(100% + 20px)"
      p="0px"
      px="10px"
      ml="-10px"
      overflowX="auto"
    >
      <Box>
        <Table
          variant="simple"
          color="gray.500"
          mb="24px"
          pb="2"
          lineHeight="15px"
        >
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  return (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      px={{
                        lg: index === 0 ? "20px" : "20px 10px",
                        md: "10px",
                        base: "10px",
                      }}
                      border={"none"}
                      fontFamily="Inter"
                      cursor="pointer"
                      textTransform="capitalize"
                      whiteSpace="nowrap"
                    >
                      <Flex
                        justifyContent="space-between"
                        align="center"
                        fontSize={{ sm: "10px", lg: "12px" }}
                        lineHeight="24px"
                        color="gray.500"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: "",
                          desc: "",
                        }[header.column.getIsSorted() as string] ?? null}
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {!isFetching &&
              table.getRowModel().rows.map((row, index) => {
                return (
                  <Fragment key={index}>
                    <Tr
                      mb={"10px"}
                      position="relative"
                      zIndex={1}
                      cursor="pointer"
                      onClick={() => {
                        onShowProjectDetails(row.original.order_id);
                      }}
                      _after={{
                        content: '""',
                        position: "absolute",
                        // border: "1px solid #DCDCDC",
                        border: isExpeditedOrder(
                          row.original.include_expedited_turnaround,
                          row.original.order_status_id
                        )
                          ? "1px solid #EE000055"
                          : "1px solid #DCDCDC",
                        bgColor: isExpeditedOrder(
                          row.original.include_expedited_turnaround,
                          row.original.order_status_id
                        )
                          ? "#EE000022"
                          : "transparent",
                        borderRadius: "10px",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: "-1",
                        transition: "all .25s ease-in-out",
                      }}
                      _hover={{
                        _after: {
                          backgroundColor: isExpeditedOrder(
                            row.original.include_expedited_turnaround,
                            row.original.order_status_id
                          )
                            ? "#EE000020"
                            : "#F1F1F1",
                          borderColor: isExpeditedOrder(
                            row.original.include_expedited_turnaround,
                            row.original.order_status_id
                          )
                            ? "#EE000055"
                            : "#F1F1F1",
                          boxShadow: "0px 2px 6px 3px rgba(0, 0, 0, 0.1)",
                        },
                      }}
                      _active={{
                        _after: {
                          boxShadow: "none",
                        },
                      }}
                    >
                      {row.getVisibleCells().map((cell, index: number) => {
                        return (
                          <Td
                            key={index}
                            padding={{
                              lg: index === 0 ? "20px" : "20px 10px",
                              md: "20px 10px",
                              base: "15px 10px",
                            }}
                            fontSize={{ sm: "14px" }}
                            minW={{
                              base: `${columnWidths[cell.column.id as keyof typeof columnWidths]}px`,
                              md: `${columnWidths[cell.column.id as keyof typeof columnWidths]}px`,
                              // lg: "auto",
                            }}
                            border="none"
                            width={
                              cell.column.id === "actionCol" ? "80px" : "auto"
                            }
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </Td>
                        );
                      })}
                    </Tr>
                    <Tr>
                      <Td py={1} borderBottom="none"></Td>
                    </Tr>
                  </Fragment>
                );
              })}
            {isFetching &&
              _.range(pageLimit).map((row) => {
                return (
                  <Fragment key={`skeleton-row-${row}`}>
                    <Tr
                      zIndex="1"
                      position="relative"
                      _after={{
                        content: '""',
                        position: "absolute",
                        border: "1px solid #DCDCDC",
                        borderRadius: "10px",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: "-1",
                        transition: "all .25s ease-in-out",
                      }}
                    >
                      {columns.map((it: any, colIndex: number) => {
                        return (
                          <Td
                            key={`skeleton-${colIndex}`}
                            padding={{
                              lg: "20px",
                              md: "20px 10px",
                              base: "15px 10px",
                            }}
                            border="none !important"
                          >
                            <Skeleton
                              height="25px"
                              colorScheme="#F3F5F5"
                              borderRadius="5px"
                            ></Skeleton>
                          </Td>
                        );
                      })}
                    </Tr>
                    <Tr>
                      <Td
                        colSpan={7}
                        paddingTop="10px"
                        paddingBottom="10px"
                        border="none !important"
                      ></Td>
                    </Tr>
                  </Fragment>
                );
              })}
          </Tbody>
        </Table>
      </Box>
    </Card>
  );
}
